import { Layer } from '@app/modules/cop/store/cop/models';
import { Dataset, DatasetSubtype, DatasetType } from './dataset.model';
import { FilterGroup } from './filters.model';
import { LinkFilterGroup } from './link-filters.model';
import { TextValueByCulture } from './translations.model';

export const SystemFieldNames = {
  address: 'address',
  borderColor: 'border_color',
  content: 'content',
  createdBy: 'created_by',
  dateCreated: 'date_created',
  dateUpdated: 'date_updated',
  details: 'details',
  emailSubject: 'email_subject',
  fillColor: 'fill_color',
  isSelectable: 'is_selectable',
  location: 'location',
  name: 'name',
  posted: 'posted_date',
  provider: 'provider',
  summary: 'summary',
  title: 'title',
  uId: 'uid',
  updatedBy: 'updated_by',
  workflowStepUId: 'workflow_step_uid',
  workflowUId: 'workflow_uid',
  zIndex: 'z_index',
  cultureCode: 'culture_code',
  externalId: 'external_id',
  parentDatasetUId: 'parent_dataset_uid',
  parentUId: 'parent_uid'
};

export interface DocumentSchema {
  fields: SchemaField[];
}

export interface SchemaField {
  uId?: string;
  systemName?: string;
  displayName?: string;
  displayNameCulture?: string;
  displayNameByCulture?: TextValueByCulture;
  dataType?: FieldDataType;
  fieldType?: SystemFieldType;
  settings?: any;
  isUniqueKey?: boolean;
  picklistValues?: PicklistValue[];
  picklistDatasetUId?: string;
  enumerationValues?: EnumerationValue[];
  enumerationRegistrationUId?: string;
  sourceUnit?: string;
  dataClassification?: DataClassification;
  durationValues?: DurationValue[];
}

export const TextFieldFormat = {
  MultiLine: 'FORMAT-TEXT-MULTILINE',
  SingleLine: 'FORMAT-TEXT-SINGLELINE'
};

export const DateFieldFormat = {
  LongDateWithTime: 'LONG-DATE-WITH-TIME',
  LongDateWithTime24Hr: 'LONG-DATE-WITH-TIME-24HR',
  DateOnly: 'DATE-ONLY',
  TimeOnly: 'TIME-ONLY',
  TimeOnly24Hr: 'TIME-ONLY-24HR'
};

export const DateFieldDefault = {
  Today: 'DATE-DEFAULT-TODAY',
  Now: 'DATE-DEFAULT-NOW',
  Timestamp: 'DATE-DEFAULT-TIMESTAMP'
};

export const PicklistFormat = {
  Radio: 'PICKLIST-FORMAT-RADIO',
  DropDownList: 'PICKLIST-FORMAT-DROPDOWNLIST',
  CheckBox: 'PICKLIST-FORMAT-CHECKBOX'
};

export const EnumerationFormat = {
  Radio: 'ENUM-FORMAT-RADIO',
  DropDownList: 'ENUM-FORMAT-DROPDOWNLIST',
  CheckBox: 'ENUM-FORMAT-CHECKBOX',
  MuiltiSelect: 'ENUM-FORMAT-MULTISELECT'
};

export const NumberFormat = {
  Number: 'NUMBER',
  Decimal: 'NUMBER_DECIMAL',
  Accounting: 'NUMBER_ACCOUNTING'
};

export interface Unit {
  label: string;
  labelAbbreviated: string;
  code: string;
}

export const DistanceUnits: Unit[] = [
  { label: 'UNITS.DISTANCE.KILOMETERS', labelAbbreviated: 'UNITS.DISTANCE.KILOMETERS-ABBREVIATED', code: 'KM' },
  { label: 'UNITS.DISTANCE.METERS', labelAbbreviated: 'UNITS.DISTANCE.METERS-ABBREVIATED', code: 'M' },
  { label: 'UNITS.DISTANCE.MILES', labelAbbreviated: 'UNITS.DISTANCE.MILES-ABBREVIATED', code: 'MI' }
];

export enum FieldDataType {
  DateTime = 1,
  Document = 3,
  DocumentCollection = 4,
  Geometry = 5,
  MultiPicklist = 6,
  Number = 7,
  Picklist = 8,
  Stream = 9,
  Text = 10,
  Url = 11,
  UId = 12,
  User = 13,
  Image = 14,
  Boolean = 15,
  Color = 16,
  Distance = 17,
  Phone = 18,
  Email = 19,
  RelatedParent = 20,
  RelatedChild = 21,
  SingleReference = 22,
  Category = 23,
  AutoIncrement = 24,
  Array = 25,
  CultureCode = 26,
  ReferenceCriteria = 27,
  Address = 28,
  Duration = 29,
  Enumeration = 30,
  BusinessUnits = 31
}

export enum DataClassification {
  NotSet = 0,
  Public = 1,
  Internal = 2,
  Confidential = 3,
  Restricted = 4
}

export enum SystemFieldType {
  None = 0,
  System = 1,
  Hidden = 2,
  Editable = 4,
  Deletable = 8,
  NotSelectable = 16
}

export interface DataType {
  label: string;
  icon: string;
  type: FieldDataType;
}

export const dataTypeLabels: string[] = [
  null,
  'DATATYPES.DATETIME',
  null,
  'DATATYPES.DOCUMENT',
  'DATATYPES.DOCUMENT-COLLECTION',
  'DATATYPES.GEOMETRY',
  'DATATYPES.MULTI-PICK-LIST',
  'DATATYPES.NUMBER',
  'DATATYPES.PICK-LIST',
  'DATATYPES.STREAM',
  'DATATYPES.TEXT',
  'DATATYPES.URL',
  'DATATYPES.UID',
  'DATATYPES.USER',
  'DATATYPES.IMAGE',
  'DATATYPES.BOOLEAN',
  'DATATYPES.COLOR',
  'DATATYPES.DISTANCE',
  'DATATYPES.PHONE',
  'DATATYPES.EMAIL',
  null,
  'DATATYPES.PARENT-REFERENCE',
  'DATATYPES.SINGLE-REFERENCE',
  'DATATYPES.CATEGORY',
  'DATATYPES.AUTO-INCREMENT',
  'DATATYPES.ARRAY',
  'DATATYPES.CULTURE-CODE',
  'DATATYPES.RELATED-FILTER',
  'DATATYPES.ADDRESS',
  'DATATYPES.DURATION',
  'DATATYPES.ENUMERATION',
  'DATATYPES.BUSINESS-UNITS'
];

export const dataTypes: DataType[] = [
  { label: 'DATATYPES.TEXT', icon: 'text_format', type: FieldDataType.Text },
  { label: 'DATATYPES.DATETIME', icon: 'today', type: FieldDataType.DateTime },
  { label: 'DATATYPES.GEOMETRY', icon: 'location_on', type: FieldDataType.Geometry },
  { label: 'DATATYPES.IMAGE', icon: 'panorama', type: FieldDataType.Image },
  { label: 'DATATYPES.PICK-LIST', icon: 'check_box_outline', type: FieldDataType.Picklist },
  { label: 'DATATYPES.MULTI-PICK-LIST', icon: 'list', type: FieldDataType.MultiPicklist },
  { label: 'DATATYPES.NUMBER', icon: 'exposure_plus_1', type: FieldDataType.Number },
  { label: 'DATATYPES.UID', icon: 'code', type: FieldDataType.UId },
  { label: 'DATATYPES.URL', icon: 'http', type: FieldDataType.Url },
  { label: 'DATATYPES.USER', icon: 'person', type: FieldDataType.User },
  { label: 'DATATYPES.BOOLEAN', icon: 'toggle_on', type: FieldDataType.Boolean },
  { label: 'DATATYPES.COLOR', icon: 'palette', type: FieldDataType.Color },
  { label: 'DATATYPES.DISTANCE', icon: 'open_in_full', type: FieldDataType.Distance },
  { label: 'DATATYPES.PHONE', icon: 'phone', type: FieldDataType.Phone },
  { label: 'DATATYPES.EMAIL', icon: 'email', type: FieldDataType.Email },
  { label: 'DATATYPES.PARENT-REFERENCE', icon: 'table_chart', type: FieldDataType.RelatedChild },
  { label: 'DATATYPES.CHILD-REFERENCE', icon: 'table_chart', type: FieldDataType.RelatedParent },
  { label: 'DATATYPES.SINGLE-REFERENCE', icon: 'repeat', type: FieldDataType.SingleReference },
  { label: 'DATATYPES.CATEGORY', icon: 'category', type: FieldDataType.Category },
  { label: 'DATATYPES.AUTO-INCREMENT', icon: 'numbers', type: FieldDataType.AutoIncrement },
  { label: 'DATATYPES.ARRAY', icon: 'text_format', type: FieldDataType.Array },
  { label: 'DATATYPES.CULTURE-CODE', icon: 'language', type: FieldDataType.CultureCode },
  { label: 'DATATYPES.RELATED-FILTER', icon: 'link', type: FieldDataType.ReferenceCriteria },
  { label: 'DATATYPES.ADDRESS', icon: 'location_on', type: FieldDataType.Address },
  { label: 'DATATYPES.DURATION', icon: 'timelapse', type: FieldDataType.Duration },
  { label: 'DATATYPES.ENUMERATION', icon: 'check_box_outline', type: FieldDataType.Enumeration },
  { label: 'DATATYPES.BUSINESS-UNITS', icon: 'group-rows', type: FieldDataType.BusinessUnits }
];

export interface PicklistValue {
  uId: string;
  value: string;
  isActive: boolean;
  name?: string;
  valueCulture?: string;
  valueByCulture?: { [cultureKey: string]: string };
}

export interface EnumerationValue {
  uId: string;
  value: number;
  isActive: boolean;
  name?: string;
  nameCulture?: string;
  nameByCulture?: { [cultureKey: string]: string };
}

export interface DurationValue {
  uId: string;
  value: number;
}

export interface DurationDetails {
  uId: string;
  days: number;
  hours: number;
  minutes: number;
}

export interface Document {
  [x: string]: any;
}

export interface DocumentsSet {
  [uId: string]: Document;
}

export interface DocumentCollection {
  documentSchema?: DocumentSchema;
  documents?: Document[];
}

export interface HistoryDocuments {
  fromDocument?: Document;
  toDocument?: Document;
}

export interface RelatedDocument {
  datasetUId?: string;
  isChild?: boolean;
  parentFieldUId?: string;
  document?: Document;
}

export interface RelatedDocumentCountsByDataset {
  targetDatasetUId?: string;
  fromDocumentUId?: string;
  targetDatasetType?: DatasetType;
  targetDatasetSubtype?: DatasetSubtype;
  count?: number;
}

export interface RelatedDocumentsRequest {
  uId: string;
  fromDataset?: Dataset;
  fromDocument?: Document;
  targetDatasetUId?: string;
  targetDataset?: Dataset;
  targetLayer?: Layer;
  targetFilterGroup?: FilterGroup;
  targetRefField?: SchemaField;
}

export const getDefaultSchemaFields = (): SchemaField[] => {
  return [];
};

export enum DocumentProximityStatus {
  Done = 0,
  Pending = -1,
  Error = -2
}
